import { useEffect, useState } from 'react';
import { X, ClipboardCheck } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchCuves, retourAtelier, estBlocageFiliere, repartirProrata } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';

// Saisie numérique du retour d'atelier : mêmes lignes que la fiche papier.
// Pour plusieurs lots réellement triturés ensemble, un total mesuré peut être réparti au
// prorata du poids net — mêmes règles d'arrondi que la « Saisie rapide » (repartirProrata),
// et jamais entre des clients différents.
export const RetourAtelierDialog = ({ of, utilisateur, onClose, onDone }) => {
  const [cuves, setCuves] = useState([]);
  const restants = of.lignes.filter((l) => l.statut !== 'Trituré' && l.statut !== 'Décompté');
  const [lignes, setLignes] = useState(restants.map((l) => ({ id: l.id, huile: '', cuveId: '' })));
  const [groupe, setGroupe] = useState([]);
  const [totalMesure, setTotalMesure] = useState('');
  const [enCours, setEnCours] = useState(false);
  const maj = (i, k, v) => setLignes((ls) => ls.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const famille = of.lignes.some((l) => l.qualite === 'Lampante') ? 'lampante' : 'noble';
  const cleTiers = (l) => (l.parcours === 'client' ? `client:${l.tiersId ?? l.tiers}` : 'fournisseurs');

  useEffect(() => {
    fetchCuves().then((cs) => setCuves(cs.filter((c) => c.id !== 'CUVE_SORTIE_DIRECTE' && (c.famille ?? 'noble') === famille)));
  }, [famille]);

  const totalHuile = lignes.reduce((s, l) => s + (Number(l.huile) || 0), 0);
  const totalOlives = restants.reduce((s, l) => s + l.poidsNet, 0);
  const lotsGroupe = restants.filter((l) => groupe.includes(l.id));

  const basculer = (lot) => {
    if (groupe.includes(lot.id)) return setGroupe((g) => g.filter((x) => x !== lot.id));
    if (lotsGroupe.length && cleTiers(lotsGroupe[0]) !== cleTiers(lot))
      return toast.error(
        'Ces lots appartiennent à des passages différents : un total d’huile mesuré ne se partage jamais entre deux clients.'
      );
    setGroupe((g) => [...g, lot.id]);
  };

  // Répartition d'un total mesuré sur les lots cochés : remplit les champs « Huile (kg) ».
  const repartir = () => {
    const { error, parts } = repartirProrata(totalMesure, lotsGroupe);
    if (error) return toast.error(error);
    setLignes((ls) => ls.map((x) => {
      const p = parts.find((y) => y.id === x.id);
      return p ? { ...x, huile: String(p.huile) } : x;
    }));
    toast.success(
      `${fmt(Number(totalMesure), 3)} kg répartis au prorata sur ${parts.length} lot(s) — somme ${fmt(
        parts.reduce((s, p) => s + p.huile, 0),
        3
      )} kg`
    );
  };

  const { user } = useAuth();
  const valider = async (e, override = false) => {
    e?.preventDefault?.();
    setEnCours(true);
    const { error, termine } = await retourAtelier(of.id, lignes.map((l) => ({ ...l, huile: Number(l.huile) || 0 })), utilisateur, { override });
    setEnCours(false);
    if (error) {
      if (!override && estBlocageFiliere(error) && estAdmin(user?.role) && window.confirm(`${error}\n\nOVERRIDE ADMINISTRATEUR : forcer quand même ? L'opération sera tracée dans le journal d'audit.`)) return valider(null, true);
      return toast.error(error);
    }
    toast.success(termine ? `OF ${of.numero} terminé — ${fmt(totalHuile, 3)} kg d'huile mis en stock` : `Retour enregistré — OF ${of.numero} partiellement traité`);
    onDone?.();
    onClose();
  };

  return (
    <div data-testid="retour-atelier-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={valider} data-testid="retour-atelier-form" className="max-h-[92vh] w-full max-w-3xl overflow-y-auto border border-slate-200 border-t-4 border-t-emerald-700 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black text-slate-900"><ClipboardCheck size={18} className="text-emerald-700" /> Retour d'atelier — OF {of.numero}</h2>
            <p className="text-xs text-slate-500">{of.decanteur ?? '—'} · reportez l'huile produite et la cuve de chaque lot depuis la fiche papier.</p>
          </div>
          <button type="button" data-testid="retour-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white"><X size={16} /></button>
        </div>

        {restants.length > 1 && (
          <div data-testid="retour-prorata" className="border-b border-slate-200 bg-slate-50 px-5 py-3">
            <p className="font-data text-[11px] font-bold uppercase tracking-[0.08em] text-slate-600">
              Lots triturés ENSEMBLE — total mesuré à répartir au prorata du poids net
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                data-testid="retour-total-mesure"
                type="number"
                step="0.001"
                min="0"
                placeholder="Total huile (kg)"
                value={totalMesure}
                onChange={(e) => setTotalMesure(e.target.value)}
                className="tabular h-10 w-36 border border-slate-300 bg-white px-2 text-center font-data text-sm outline-none focus:border-slate-900"
              />
              <button
                type="button"
                data-testid="retour-repartir"
                disabled={!(Number(totalMesure) > 0) || lotsGroupe.length === 0}
                onClick={repartir}
                className="h-10 border border-slate-900 bg-white px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400"
              >
                Répartir sur les {lotsGroupe.length} lot(s) coché(s)
              </button>
              <span className="font-data text-[11px] text-slate-500">
                Les lots non cochés gardent leur saisie manuelle — aucune huile n'est réattribuée.
              </span>
            </div>
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {restants.map((l, i) => (
            <div key={l.id} data-testid={`retour-ligne-${l.id}`} className="grid grid-cols-1 items-center gap-3 px-5 py-3 sm:grid-cols-[30px_1fr_150px_220px]">
              {restants.length > 1 ? (
                <input
                  type="checkbox"
                  data-testid={`retour-coche-${l.id}`}
                  checked={groupe.includes(l.id)}
                  onChange={() => basculer(l)}
                  className="h-4 w-4 accent-slate-900"
                />
              ) : (
                <span />
              )}
              <div>
                <p className="font-data text-sm font-semibold text-slate-900">{l.ordre}. {l.id} · {l.tiers}</p>
                <p className="font-data text-[11px] text-slate-500">{l.qualite} · {l.mode} · {fmt(l.poidsNet)} kg d'olives{l.devenir === 'sortie' ? ' · sortie immédiate → écran Trituration' : ''}</p>
              </div>
              <input data-testid={`retour-huile-${l.id}`} type="number" step="0.001" min="0" placeholder="Huile (kg)" value={lignes[i].huile} onChange={(e) => maj(i, 'huile', e.target.value)} className="h-11 border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white" />
              <select data-testid={`retour-cuve-${l.id}`} value={lignes[i].cuveId} onChange={(e) => maj(i, 'cuveId', e.target.value)} className="h-11 border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white">
                <option value="">— Cuve de destination —</option>
                {cuves.map((c) => <option key={c.id} value={c.id}>{c.nom} · reste {fmt(c.capacite - c.volume)} {c.unite}</option>)}
              </select>
            </div>
          ))}
          {restants.length === 0 && <p className="px-5 py-6 text-sm text-slate-500">Tous les lots de cet OF sont déjà triturés.</p>}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-900 px-5 py-3 text-white">
          <span className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-300">Total {fmt(totalOlives)} kg olives → {fmt(totalHuile, 3)} kg huile</span>
          <span data-testid="retour-rendement" className="font-data text-xl font-semibold">{totalOlives > 0 && totalHuile > 0 ? `${fmt((totalHuile / totalOlives) * 100, 2)} %` : '—'}</span>
        </div>
        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button type="submit" data-testid="retour-valider" disabled={enCours} className="h-11 flex-1 bg-emerald-700 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white hover:bg-emerald-800 disabled:bg-slate-300">{enCours ? 'Enregistrement…' : 'Valider la trituration & mettre à jour les stocks'}</button>
          <button type="button" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">Annuler</button>
        </div>
      </form>
    </div>
  );
};
